import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Scissors } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import { errorAlert } from '@xdocs/schemas';
import type { JobAccepted } from '@xdocs/schemas';
import { requestTyped } from '@xdocs/ws-protocol';
import { Badge, ConfirmDialog, ListRow, Select, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import { useJobSnapshot } from '../jobs/index.js';
import { useWs } from '../../ws/WsProvider.js';
import { EnhancementPanelShell } from './EnhancementPanelShell.js';
import type { EnhancementPanelPhase } from './EnhancementPanelShell.js';
import { useActionEstimate } from './useActionEstimate.js';
import { useEnhancementT } from './useEnhancementT.js';
import styles from './CutContinuePanel.module.css';

export interface CutContinuePanelProps {
  documentId: string;
  /** Required — names the asset in the destructive confirm + toasts (§16.1). */
  documentTitle: string;
  versionId: string;
  /** The manuscript's current chapters, order-ascending (F3 cut-point picker). */
  chapters: readonly { id: string; title: string }[];
  className?: string | undefined;
}

/**
 * F3 — cut-the-end, keep rolling (PRO). Self-contained trigger + panel.
 * FEATURES.md F3 AC: "preview the truncation so the destructive nature is
 * obvious" (inline warning) + "confirm (destructive copy)" — the inline
 * warning informs, a separate `ConfirmDialog` (asset-named, destructive)
 * gates the actual `document.cut` call. Versions are immutable (DAG), so
 * nothing is actually lost — the confirm still fires because the NEW
 * version supersedes everything after the cut point in the reading flow.
 */
export function CutContinuePanel({ documentId, documentTitle, versionId, chapters, className }: CutContinuePanelProps): ReactElement {
  const { t } = useEnhancementT();
  const { locale } = useI18n();
  const { push: pushToast } = useToast();
  const { wsClient } = useWs();

  const [open, setOpen] = useState(false);
  // Only ever 'form' | 'starting' | 'upsell' | 'running' — terminal
  // 'done'/'error' is DERIVED from the live job snapshot below (react-hooks
  // purity: never setState synchronously inside an effect).
  const [phase, setPhase] = useState<EnhancementPanelPhase>('form');
  const [fromChapterId, setFromChapterId] = useState<string | null>(chapters[0]?.id ?? null);
  const [direction, setDirection] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { estimate } = useActionEstimate('cut.estimate', open && fromChapterId ? { documentId, versionId, fromChapterId } : null);
  const { snapshot } = useJobSnapshot(jobId);

  const jobSucceeded = phase === 'running' && snapshot?.job.status === 'succeeded';
  const jobFailedTerminal =
    phase === 'running' && snapshot?.job.status === 'failed' && snapshot.job.attempts >= snapshot.job.maxAttempts;
  const displayPhase: EnhancementPanelPhase = jobSucceeded ? 'done' : jobFailedTerminal ? 'error' : phase;
  const displayErrorMessage = jobFailedTerminal ? (snapshot?.job.lastError ?? 'errors:code.INTERNAL') : submitError;

  const toastedForJobRef = useRef<string | null>(null);
  useEffect(() => {
    if (jobSucceeded && jobId && toastedForJobRef.current !== jobId) {
      pushToast(assetToast(locale, 'success', t('enhancement:cut.success'), documentTitle));
      toastedForJobRef.current = jobId;
    }
  }, [jobSucceeded, jobId, pushToast, locale, t, documentTitle]);

  function resetForm(): void {
    setPhase('form');
    setJobId(null);
    setSubmitError(null);
  }

  function handleOpenChange(next: boolean): void {
    setOpen(next);
    if (!next) {
      resetForm();
      setConfirmOpen(false);
    }
  }

  function runCut(): void {
    if (!fromChapterId) return;
    setPhase('starting');
    requestTyped(wsClient, 'document.cut', { documentId, versionId, fromChapterId, instructions: direction.trim() || undefined })
      .then((accepted: JobAccepted) => {
        setJobId(accepted.jobId);
        setPhase('running');
      })
      .catch((err: unknown) => {
        const parsed = errorAlert.safeParse(err);
        if (parsed.success && parsed.data.code === 'TIER_REQUIRED') {
          setPhase('upsell');
          return;
        }
        setPhase('error');
        setSubmitError(parsed.success ? parsed.data.message : 'errors:code.INTERNAL');
      });
  }

  const creditsLabel = estimate ? t('enhancement:cost.credits', { credits: estimate.credits }) : undefined;

  return (
    <>
      <ListRow
        icon={<Scissors size={16} aria-hidden="true" />}
        title={t('enhancement:cut.title')}
        trailing={<Badge tone="pro">{t('enhancement:pro.badge')}</Badge>}
        onClick={() => setOpen(true)}
        className={cn(className)}
      />
      <EnhancementPanelShell
        open={open}
        onOpenChange={handleOpenChange}
        title={t('enhancement:cut.title')}
        blurb={t('enhancement:cut.blurb')}
        phase={displayPhase}
        jobId={jobId}
        doneTitle={t('enhancement:cut.doneTitle')}
        errorMessage={displayErrorMessage}
        ctaLabel={t('enhancement:cut.cta')}
        creditsLabel={creditsLabel}
        ctaDisabled={!fromChapterId}
        onStart={() => setConfirmOpen(true)}
        onMaybeLater={() => handleOpenChange(false)}
        onDone={() => handleOpenChange(false)}
        onRetryFromError={resetForm}
      >
        {chapters.length === 0 ? (
          <p className={styles['empty']}>{t('enhancement:cut.noChapters')}</p>
        ) : (
          <div className={styles['field']}>
            <Select
              label={t('enhancement:cut.cutAfterChapter')}
              value={fromChapterId ?? ''}
              onChange={(event) => setFromChapterId(event.target.value)}
              options={chapters.map((chapter) => ({ value: chapter.id, label: chapter.title }))}
            />
            <Textarea
              label={t('enhancement:cut.direction')}
              placeholder={t('enhancement:cut.directionPlaceholder')}
              value={direction}
              onChange={(event) => setDirection(event.target.value)}
            />
            <p className={styles['warning']}>{t('enhancement:cut.warning')}</p>
          </div>
        )}
      </EnhancementPanelShell>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        action={t('enhancement:cut.confirm.action')}
        assetName={documentTitle}
        destructive
        onConfirm={() => {
          setConfirmOpen(false);
          runCut();
        }}
      />
    </>
  );
}
